import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import MatchesPage from "@/pages/MatchesPage";
import StatsPage from "@/pages/StatsPage";
import ProfilePage from "@/pages/ProfilePage";
import MatchDetailPage from "@/pages/MatchDetailPage";
import LoginPage from "@/pages/LoginPage";
import FindMatchPage from "@/pages/FindMatchPage";
import CreateMatchPage from "@/pages/CreateMatchPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import BadgesPage from "@/pages/BadgesPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";

// Components
import BottomNav from "@/components/BottomNav";
import FlashBanner from "@/components/FlashBanner";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import CityOnboardingModal from "@/components/CityOnboardingModal";
import OnboardingCarousel from "@/components/OnboardingCarousel";
import MaintenanceGate from "@/components/MaintenanceGate";
import PushGateModal from "@/components/PushGateModal";

// Auth context + push setup
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { resubscribeIfGranted } from "@/services/push";

const TOAST_OPTIONS = {
  style: { background: "#111", border: "1px solid #333", color: "#fff" },
};

function AppContent() {
  const [activeTab, setActiveTab] = useState("home");
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  // Register the Service Worker unconditionally on load (silent, no permission
  // prompt) so navigator.serviceWorker.ready resolves instantly when a user later
  // grants permission — avoids the mobile micro-delay that drops the prompt.
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch((e) => console.error("SW register failed", e));
    }
  }, []);

  // Re-subscribe silently ONLY if the user already granted permission (no prompt).
  // Mobile browsers block requestPermission() outside a user gesture — never auto-prompt.
  useEffect(() => {
    if (isAuthenticated) {
      resubscribeIfGranted().catch(() => {});
    }
  }, [isAuthenticated]);

  // Global live-presence socket: EVERY authenticated user opens it so the admin
  // counter reflects all online users (not just dashboard viewers).
  // Always wss:// on the serving host — never localhost / ws://.
  useEffect(() => {
    if (!isAuthenticated) return;
    let closed = false;
    let timer = null;
    let ws = null;
    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(`wss://${window.location.host}/api/ws/presence`);
      } catch (_) {
        timer = setTimeout(connect, 3000);
        return;
      }
      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          if (typeof d.online === "number") {
            window.__matchupOnline = d.online;
            window.dispatchEvent(new CustomEvent("matchup:presence", { detail: d.online }));
          }
        } catch (_) {
          /* ignore malformed frames */
        }
      };
      ws.onclose = () => {
        if (!closed) timer = setTimeout(connect, 3000);
      };
      ws.onerror = () => {
        try { ws.close(); } catch (_) { /* noop */ }
      };
    };
    connect();
    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      if (ws) { try { ws.close(); } catch (_) { /* noop */ } }
    };
  }, [isAuthenticated]);

  // CRITICAL: Handle Google OAuth callback synchronously (URL fragment session_id)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallbackPage />;
  }

  if (!isAuthenticated) {
    return (
      <>
        <OnboardingCarousel />
        <LoginPage />
      </>
    );
  }

  return (
    <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      <FlashBanner />
      <div style={{ flex: 1, paddingBottom: "calc(80px + env(safe-area-inset-bottom, 0px))" }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/matches" element={<MatchesPage />} />
          <Route path="/match/:id" element={<MatchDetailPage />} />
          <Route path="/find" element={<FindMatchPage />} />
          <Route path="/creer-un-match" element={<CreateMatchPage />} />
          <Route path="/admin-dashboard" element={<AdminDashboardPage />} />
          <Route path="/badges" element={<BadgesPage />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/:id" element={<ProfilePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
      <PWAInstallBanner />
      <CityOnboardingModal />
      <PushGateModal />
      <MaintenanceGate />
    </div>
  );
}

function App() {
  return (
    <div className="App" style={{ minHeight: "100dvh", backgroundColor: "#000", color: "#fff" }}>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <BrowserRouter>
          <AuthProvider>
            <SettingsProvider>
              <AppContent />
              <Toaster position="top-center" toastOptions={TOAST_OPTIONS} />
            </SettingsProvider>
          </AuthProvider>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </div>
  );
}

export default App;
